import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Store, ArrowRight, Loader2, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

/** RoutePlanCard — mobile home tile showing today's PJP + attendance +
 * nearby unvisited outlets. Reads /api/field/today.  */
export default function RoutePlanCard() {
  const nav = useNavigate();
  const [plan, setPlan] = useState(null);
  const [gps, setGps] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (latlng) => {
    setLoading(true);
    try {
      const params = latlng ? { lat: latlng.lat, lng: latlng.lng } : {};
      const { data } = await api.get("/field/today", { params });
      setPlan(data);
    } catch (_e) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  // Get GPS once + load
  useEffect(() => {
    if (!navigator.geolocation) return load(null);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const g = { lat: p.coords.latitude, lng: p.coords.longitude };
        setGps(g); load(g);
      },
      () => load(null),
      { timeout: 6000, maximumAge: 60000 },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const counts = plan?.counts || { planned: 0, done: 0, pending: 0, skipped: 0 };
  const next = plan?.next_recommended;

  return (
    <Card className="border-neutral-200 rounded-2xl overflow-hidden" data-testid="route-plan-card">
      <CardContent className="p-4 space-y-3">
        {loading && <div className="text-neutral-400 text-sm py-4 text-center"><Loader2 className="h-4 w-4 inline animate-spin" /> Loading route…</div>}

        {/* Header (title/date + attendance chip) and the Planned/Done/
            Pending/Skip stat tiles were dropped — "Today's Beat" (linked
            just below on the Home screen) already shows that same
            breakdown in more detail with a full tappable list. This card
            now only keeps what Today's Beat doesn't have: an actionable
            next-outlet suggestion and nearby-unplanned outlets. */}
        {!loading && plan && (
          <>
            {/* Next tile */}
            {next ? (
              <div className="mt-1 rounded-2xl tulsi-gradient text-white p-4"
                   data-testid="rp-next-tile">
                <div className="text-[11px] uppercase tracking-widest text-white/80">Next up</div>
                <div className="text-lg font-bold leading-tight mt-1">{next.name}</div>
                <div className="text-xs text-white/90 mt-1 flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {next.distance_from_current_m != null
                    ? `${next.distance_from_current_m}m away · ${next.code}`
                    : next.code}
                </div>
                <Button variant="secondary" size="sm"
                        onClick={() => nav(`/visits/start/${next.retailer_id}`)}
                        className="mt-3 h-9 rounded-lg text-[#E5197F] font-semibold"
                        data-testid="rp-start-visit-btn">
                  Start visit <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            ) : counts.planned === 0 ? (
              <div className="rounded-xl bg-neutral-50 border border-dashed border-neutral-200 p-4 text-center">
                <Store className="h-6 w-6 mx-auto text-neutral-300" />
                <div className="text-sm text-neutral-500 mt-2">No planned outlets today</div>
                <div className="text-[11px] text-neutral-400">Contact your ASM to set your PJP</div>
              </div>
            ) : (
              <div className="text-sm text-neutral-500 py-3 text-center">All planned outlets done ✓</div>
            )}

            {/* Nearby unvisited */}
            {plan.nearby_unvisited?.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-widest text-neutral-400 mt-2 mb-2">
                  Nearby (not planned)
                </div>
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 snap-x">
                  {plan.nearby_unvisited.slice(0, 8).map((n) => (
                    <div key={n.retailer_id}
                         className="shrink-0 rounded-xl border border-neutral-200 bg-white px-3 py-2 min-w-[140px] snap-start"
                         data-testid={`rp-nearby-${n.code}`}>
                      <div className="text-[11px] text-neutral-400">{n.code}</div>
                      <div className="text-sm font-semibold text-neutral-900 truncate">{n.name}</div>
                      <div className="text-[10px] text-neutral-500 mt-1">{n.distance_m}m</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

